/**
 * BrightPath - ExpenseForm Component
 * BP-04-04: Expenses Frontend
 */

import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  DollarSign,
  Calendar,
  Tag,
  FileText,
  Receipt,
  Shield,
  Repeat,
  AlertCircle,
  Upload,
  X,
  Plus,
} from 'lucide-react';
import type {
  ExpenseCreate,
  ExpenseUpdate,
  Expense,
  ExpenseCategory,
  ClaimStatus,
  RecurrencePattern,
} from '../types';
import { EXPENSE_CATEGORIES, CLAIM_STATUS_LABELS, RECURRENCE_PATTERNS } from '../types';
import { useChildren } from '@/features/children/hooks';
import { formatCurrency } from '@/core/lib/utils';

// Validation schema
const expenseSchema = z.object({
  childId: z.string().nullable().optional(),
  category: z.enum([
    'medical',
    'therapy',
    'equipment',
    'medication',
    'transportation',
    'home_modification',
    'legal',
    'insurance',
    'other',
  ]),
  subcategory: z.string().nullable().optional(),
  description: z.string().min(1, 'Description is required').max(500),
  amount: z.number().min(0.01, 'Amount must be greater than 0'),
  currency: z.string().default('USD'),
  expenseDate: z.string().min(1, 'Date is required'),
  vendorName: z.string().nullable().optional(),
  paymentMethod: z.string().nullable().optional(),
  isInsuranceCovered: z.boolean().default(false),
  insuranceAmount: z.number().nullable().optional(),
  outOfPocket: z.number().optional(),
  claimNumber: z.string().nullable().optional(),
  claimStatus: z
    .enum(['not_submitted', 'submitted', 'in_review', 'approved', 'denied', 'appealed', 'partial'])
    .nullable()
    .optional(),
  isTaxDeductible: z.boolean().default(false),
  taxCategory: z.string().nullable().optional(),
  receiptDocumentId: z.string().nullable().optional(),
  isRecurring: z.boolean().default(false),
  recurrencePattern: z.enum(['weekly', 'biweekly', 'monthly', 'quarterly', 'annually']).nullable().optional(),
  recurrenceEndDate: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
  tags: z.array(z.string()).default([]),
});

type ExpenseFormData = z.infer<typeof expenseSchema>;

interface ExpenseFormProps {
  familyId: string;
  expense?: Expense;
  onSubmit: (data: ExpenseCreate | ExpenseUpdate) => Promise<void>;
  onCancel: () => void;
  isSubmitting?: boolean;
}

const PAYMENT_METHODS = [
  'Credit Card',
  'Debit Card',
  'Cash',
  'Check',
  'HSA',
  'FSA',
  'Insurance Direct',
  'Wire Transfer',
  'Other',
];

const TAX_CATEGORIES = [
  'Medical Expenses',
  'Medical Equipment',
  'Prescription Medications',
  'Transportation for Medical Care',
  'Home Medical Equipment',
  'Therapy Services',
  'Long-term Care',
  'Other Medical',
];

export function ExpenseForm({
  familyId,
  expense,
  onSubmit,
  onCancel,
  isSubmitting = false,
}: ExpenseFormProps) {
  const { data: children } = useChildren(familyId);
  const [tagInput, setTagInput] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);

  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<ExpenseFormData>({
    resolver: zodResolver(expenseSchema),
    defaultValues: {
      childId: expense?.childId || null,
      category: expense?.category || 'medical',
      subcategory: expense?.subcategory || null,
      description: expense?.description || '',
      amount: expense?.amount || 0,
      currency: expense?.currency || 'USD',
      expenseDate: expense?.expenseDate || new Date().toISOString().split('T')[0],
      vendorName: expense?.vendorName || null,
      paymentMethod: expense?.paymentMethod || null,
      isInsuranceCovered: expense?.isInsuranceCovered || false,
      insuranceAmount: expense?.insuranceAmount || null,
      outOfPocket: expense?.outOfPocket || 0,
      claimNumber: expense?.claimNumber || null,
      claimStatus: expense?.claimStatus || null,
      isTaxDeductible: expense?.isTaxDeductible || false,
      taxCategory: expense?.taxCategory || null,
      receiptDocumentId: expense?.receiptDocumentId || null,
      isRecurring: expense?.isRecurring || false,
      recurrencePattern: expense?.recurrencePattern || null,
      recurrenceEndDate: expense?.recurrenceEndDate || null,
      notes: expense?.notes || null,
      tags: expense?.tags || [],
    },
  });

  const watchAmount = watch('amount');
  const watchIsInsuranceCovered = watch('isInsuranceCovered');
  const watchInsuranceAmount = watch('insuranceAmount');
  const watchIsRecurring = watch('isRecurring');
  const watchIsTaxDeductible = watch('isTaxDeductible');
  const watchTags = watch('tags');

  // Calculate out of pocket automatically
  useEffect(() => {
    const amount = watchAmount || 0;
    const insuranceAmount = watchIsInsuranceCovered && watchInsuranceAmount ? watchInsuranceAmount : 0;
    setValue('outOfPocket', Math.max(0, amount - insuranceAmount));
  }, [watchAmount, watchIsInsuranceCovered, watchInsuranceAmount, setValue]);

  const handleAddTag = () => {
    if (tagInput.trim() && !watchTags.includes(tagInput.trim())) {
      setValue('tags', [...watchTags, tagInput.trim()]);
      setTagInput('');
    }
  };

  const handleRemoveTag = (tag: string) => {
    setValue(
      'tags',
      watchTags.filter((t) => t !== tag)
    );
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  const onFormSubmit = async (data: ExpenseFormData) => {
    await onSubmit(data);
  };

  return (
    <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-6">
      {/* Basic Info */}
      <div className="bg-white rounded-xl border border-warm-gray/10 p-6">
        <h3 className="text-lg font-semibold text-navy mb-4">Basic Information</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-navy mb-2">Category *</label>
            <Controller
              name="category"
              control={control}
              render={({ field }) => (
                <select
                  {...field}
                  className="w-full px-4 py-3 bg-cream rounded-lg border-2 border-transparent focus:border-sage focus:outline-none transition-colors"
                >
                  {(Object.entries(EXPENSE_CATEGORIES) as [ExpenseCategory, typeof EXPENSE_CATEGORIES[ExpenseCategory]][]).map(
                    ([value, config]) => (
                      <option key={value} value={value}>
                        {config.label}
                      </option>
                    )
                  )}
                </select>
              )}
            />
            {errors.category && (
              <p className="text-red-500 text-sm mt-1">{errors.category.message}</p>
            )}
          </div>

          {/* Child */}
          <div>
            <label className="block text-sm font-medium text-navy mb-2">Child (Optional)</label>
            <Controller
              name="childId"
              control={control}
              render={({ field }) => (
                <select
                  {...field}
                  value={field.value || ''}
                  onChange={(e) => field.onChange(e.target.value || null)}
                  className="w-full px-4 py-3 bg-cream rounded-lg border-2 border-transparent focus:border-sage focus:outline-none transition-colors"
                >
                  <option value="">Family-wide expense</option>
                  {children?.map((child) => (
                    <option key={child.id} value={child.id}>
                      {child.firstName} {child.lastName}
                    </option>
                  ))}
                </select>
              )}
            />
          </div>

          {/* Description */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-navy mb-2">Description *</label>
            <div className="relative">
              <FileText className="absolute left-4 top-3.5 w-5 h-5 text-warm-gray" />
              <input
                type="text"
                {...register('description')}
                placeholder="What was this expense for?"
                className="w-full pl-12 pr-4 py-3 bg-cream rounded-lg border-2 border-transparent focus:border-sage focus:outline-none transition-colors"
              />
            </div>
            {errors.description && (
              <p className="text-red-500 text-sm mt-1">{errors.description.message}</p>
            )}
          </div>

          {/* Amount */}
          <div>
            <label className="block text-sm font-medium text-navy mb-2">Amount *</label>
            <div className="relative">
              <DollarSign className="absolute left-4 top-3.5 w-5 h-5 text-warm-gray" />
              <Controller
                name="amount"
                control={control}
                render={({ field }) => (
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    {...field}
                    onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                    placeholder="0.00"
                    className="w-full pl-12 pr-4 py-3 bg-cream rounded-lg border-2 border-transparent focus:border-sage focus:outline-none transition-colors"
                  />
                )}
              />
            </div>
            {errors.amount && (
              <p className="text-red-500 text-sm mt-1">{errors.amount.message}</p>
            )}
          </div>

          {/* Date */}
          <div>
            <label className="block text-sm font-medium text-navy mb-2">Date *</label>
            <div className="relative">
              <Calendar className="absolute left-4 top-3.5 w-5 h-5 text-warm-gray" />
              <input
                type="date"
                {...register('expenseDate')}
                className="w-full pl-12 pr-4 py-3 bg-cream rounded-lg border-2 border-transparent focus:border-sage focus:outline-none transition-colors"
              />
            </div>
            {errors.expenseDate && (
              <p className="text-red-500 text-sm mt-1">{errors.expenseDate.message}</p>
            )}
          </div>

          {/* Vendor */}
          <div>
            <label className="block text-sm font-medium text-navy mb-2">Vendor/Provider</label>
            <div className="relative">
              <Receipt className="absolute left-4 top-3.5 w-5 h-5 text-warm-gray" />
              <input
                type="text"
                {...register('vendorName')}
                placeholder="Doctor, pharmacy, store..."
                className="w-full pl-12 pr-4 py-3 bg-cream rounded-lg border-2 border-transparent focus:border-sage focus:outline-none transition-colors"
              />
            </div>
          </div>

          {/* Payment Method */}
          <div>
            <label className="block text-sm font-medium text-navy mb-2">Payment Method</label>
            <Controller
              name="paymentMethod"
              control={control}
              render={({ field }) => (
                <select
                  {...field}
                  value={field.value || ''}
                  onChange={(e) => field.onChange(e.target.value || null)}
                  className="w-full px-4 py-3 bg-cream rounded-lg border-2 border-transparent focus:border-sage focus:outline-none transition-colors"
                >
                  <option value="">Select payment method</option>
                  {PAYMENT_METHODS.map((method) => (
                    <option key={method} value={method}>
                      {method}
                    </option>
                  ))}
                </select>
              )}
            />
          </div>
        </div>
      </div>

      {/* Insurance */}
      <div className="bg-white rounded-xl border border-warm-gray/10 p-6">
        <div className="flex items-center gap-3 mb-4">
          <Shield className="w-5 h-5 text-sage" />
          <h3 className="text-lg font-semibold text-navy">Insurance Coverage</h3>
        </div>

        <div className="space-y-4">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              {...register('isInsuranceCovered')}
              className="w-5 h-5 rounded border-warm-gray/30 text-sage focus:ring-sage"
            />
            <span className="text-navy">This expense is covered by insurance</span>
          </label>

          {watchIsInsuranceCovered && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t border-warm-gray/10">
              <div>
                <label className="block text-sm font-medium text-navy mb-2">Insurance Paid</label>
                <div className="relative">
                  <DollarSign className="absolute left-4 top-3.5 w-5 h-5 text-warm-gray" />
                  <Controller
                    name="insuranceAmount"
                    control={control}
                    render={({ field }) => (
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        {...field}
                        value={field.value || ''}
                        onChange={(e) => field.onChange(parseFloat(e.target.value) || null)}
                        placeholder="0.00"
                        className="w-full pl-12 pr-4 py-3 bg-cream rounded-lg border-2 border-transparent focus:border-sage focus:outline-none transition-colors"
                      />
                    )}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-navy mb-2">Claim Number</label>
                <input
                  type="text"
                  {...register('claimNumber')}
                  placeholder="CLM-123456"
                  className="w-full px-4 py-3 bg-cream rounded-lg border-2 border-transparent focus:border-sage focus:outline-none transition-colors"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-navy mb-2">Claim Status</label>
                <Controller
                  name="claimStatus"
                  control={control}
                  render={({ field }) => (
                    <select
                      {...field}
                      value={field.value || ''}
                      onChange={(e) => field.onChange(e.target.value || null)}
                      className="w-full px-4 py-3 bg-cream rounded-lg border-2 border-transparent focus:border-sage focus:outline-none transition-colors"
                    >
                      <option value="">Select status</option>
                      {(Object.entries(CLAIM_STATUS_LABELS) as [ClaimStatus, typeof CLAIM_STATUS_LABELS[ClaimStatus]][]).map(
                        ([value, config]) => (
                          <option key={value} value={value}>
                            {config.label}
                          </option>
                        )
                      )}
                    </select>
                  )}
                />
              </div>
            </div>
          )}

          {/* Out of Pocket Summary */}
          <div className="flex items-center justify-between p-4 bg-terracotta/10 rounded-lg">
            <span className="font-medium text-navy">Out of Pocket Amount</span>
            <span className="text-xl font-bold text-terracotta">
              {formatCurrency(watch('outOfPocket') || 0)}
            </span>
          </div>
        </div>
      </div>

      {/* Tax Deductible */}
      <div className="bg-white rounded-xl border border-warm-gray/10 p-6">
        <div className="flex items-center gap-3 mb-4">
          <Receipt className="w-5 h-5 text-sage" />
          <h3 className="text-lg font-semibold text-navy">Tax Information</h3>
        </div>

        <div className="space-y-4">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              {...register('isTaxDeductible')}
              className="w-5 h-5 rounded border-warm-gray/30 text-sage focus:ring-sage"
            />
            <span className="text-navy">This is a tax-deductible expense</span>
          </label>

          {watchIsTaxDeductible && (
            <div className="pt-4 border-t border-warm-gray/10">
              <label className="block text-sm font-medium text-navy mb-2">Tax Category</label>
              <Controller
                name="taxCategory"
                control={control}
                render={({ field }) => (
                  <select
                    {...field}
                    value={field.value || ''}
                    onChange={(e) => field.onChange(e.target.value || null)}
                    className="w-full px-4 py-3 bg-cream rounded-lg border-2 border-transparent focus:border-sage focus:outline-none transition-colors"
                  >
                    <option value="">Select category</option>
                    {TAX_CATEGORIES.map((cat) => (
                      <option key={cat} value={cat}>
                        {cat}
                      </option>
                    ))}
                  </select>
                )}
              />
            </div>
          )}
        </div>
      </div>

      {/* Advanced Options Toggle */}
      <button
        type="button"
        onClick={() => setShowAdvanced(!showAdvanced)}
        className="flex items-center gap-2 text-sage hover:text-sage-dark transition-colors"
      >
        <Plus className={`w-4 h-4 transition-transform ${showAdvanced ? 'rotate-45' : ''}`} />
        <span>{showAdvanced ? 'Hide' : 'Show'} Advanced Options</span>
      </button>

      {/* Advanced Options */}
      {showAdvanced && (
        <>
          {/* Recurring */}
          <div className="bg-white rounded-xl border border-warm-gray/10 p-6">
            <div className="flex items-center gap-3 mb-4">
              <Repeat className="w-5 h-5 text-sage" />
              <h3 className="text-lg font-semibold text-navy">Recurring Expense</h3>
            </div>

            <div className="space-y-4">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  {...register('isRecurring')}
                  className="w-5 h-5 rounded border-warm-gray/30 text-sage focus:ring-sage"
                />
                <span className="text-navy">This is a recurring expense</span>
              </label>

              {watchIsRecurring && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-warm-gray/10">
                  <div>
                    <label className="block text-sm font-medium text-navy mb-2">Frequency</label>
                    <Controller
                      name="recurrencePattern"
                      control={control}
                      render={({ field }) => (
                        <select
                          {...field}
                          value={field.value || ''}
                          onChange={(e) => field.onChange(e.target.value || null)}
                          className="w-full px-4 py-3 bg-cream rounded-lg border-2 border-transparent focus:border-sage focus:outline-none transition-colors"
                        >
                          <option value="">Select frequency</option>
                          {(Object.entries(RECURRENCE_PATTERNS) as [RecurrencePattern, string][]).map(
                            ([value, label]) => (
                              <option key={value} value={value}>
                                {label}
                              </option>
                            )
                          )}
                        </select>
                      )}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-navy mb-2">End Date</label>
                    <input
                      type="date"
                      {...register('recurrenceEndDate')}
                      className="w-full px-4 py-3 bg-cream rounded-lg border-2 border-transparent focus:border-sage focus:outline-none transition-colors"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Tags */}
          <div className="bg-white rounded-xl border border-warm-gray/10 p-6">
            <div className="flex items-center gap-3 mb-4">
              <Tag className="w-5 h-5 text-sage" />
              <h3 className="text-lg font-semibold text-navy">Tags</h3>
            </div>

            <div className="space-y-3">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Add a tag..."
                  className="flex-1 px-4 py-2 bg-cream rounded-lg border-2 border-transparent focus:border-sage focus:outline-none transition-colors"
                />
                <button
                  type="button"
                  onClick={handleAddTag}
                  className="px-4 py-2 bg-sage text-white rounded-lg hover:bg-sage-dark transition-colors"
                >
                  Add
                </button>
              </div>

              {watchTags.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {watchTags.map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center gap-1 px-3 py-1 bg-cream-dark rounded-full text-sm"
                    >
                      {tag}
                      <button
                        type="button"
                        onClick={() => handleRemoveTag(tag)}
                        className="hover:text-red-500"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Notes */}
          <div className="bg-white rounded-xl border border-warm-gray/10 p-6">
            <label className="block text-sm font-medium text-navy mb-2">Notes</label>
            <textarea
              {...register('notes')}
              rows={3}
              placeholder="Any additional notes about this expense..."
              className="w-full px-4 py-3 bg-cream rounded-lg border-2 border-transparent focus:border-sage focus:outline-none transition-colors resize-none"
            />
          </div>
        </>
      )}

      {/* Form Actions */}
      <div className="flex items-center justify-end gap-3 pt-4">
        <button
          type="button"
          onClick={onCancel}
          className="px-6 py-3 text-navy hover:bg-cream-dark rounded-lg transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className="px-6 py-3 bg-sage text-white rounded-lg hover:bg-sage-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {isSubmitting ? (
            <>
              <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              Saving...
            </>
          ) : expense ? (
            'Update Expense'
          ) : (
            'Add Expense'
          )}
        </button>
      </div>
    </form>
  );
}

export default ExpenseForm;
